import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Archive,
  ArrowLeft,
  Circle,
  MessageSquare,
  MoreHorizontal,
  Send,
} from 'lucide-react';
// Archive used in dropdown menu (disabled), Circle in header, MessageSquare in empty state & dropdown,
// MoreHorizontal in actions, Send in send button, ArrowLeft in mobile back button
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
// Card components available if stats cards are added later
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { chatKeys, useConversations, useMessages, useSendMessage } from '@/hooks/useChats';
import { getSocket } from '@/sockets/socket';
import type { Conversation, Message } from '@/types/chat.types';

export const Route = createFileRoute('/_authenticated/chats')({
  component: ChatsPage,
});

type StatusFilter = 'all' | 'open' | 'closed' | 'archived';

const statusBadge: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline' }> = {
  open: { label: 'Abierto', variant: 'success' },
  closed: { label: 'Cerrado', variant: 'secondary' },
  archived: { label: 'Archivado', variant: 'outline' },
};

function ChatsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const statusParam = status === 'all' ? undefined : status;
  const { data: list, isLoading } = useConversations({ status: statusParam, search });
  const { data: messages } = useMessages(selectedId || undefined);
  const sendMutation = useSendMessage();
  const queryClient = useQueryClient();

  const selected = useMemo(() => list?.data?.find((c) => c.id === selectedId) ?? null, [list, selectedId]);

  // Socket.io real-time messages
  useEffect(() => {
    const socket = getSocket();
    const onNewMessage = (msg: Message) => {
      if (msg.conversationId === selectedId) {
        queryClient.invalidateQueries({ queryKey: chatKeys.messages(selectedId) });
      }
      // Refresca lastMessagePreview/unread en la lista de conversaciones
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    };
    const onNewConversation = () => {
      toast('Nueva conversación recibida', { icon: '💬' });
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    };
    socket.on('message.new', onNewMessage);
    socket.on('conversation.new', onNewConversation);
    return () => {
      socket.off('message.new', onNewMessage);
      socket.off('conversation.new', onNewConversation);
    };
  }, [selectedId, queryClient]);

  // Join selected conversation room
  useEffect(() => {
    if (!selectedId) return;
    const socket = getSocket();
    socket.emit('conversation.join', { conversationId: selectedId });
  }, [selectedId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const rows: Conversation[] = list?.data ?? [];

  const columns = useMemo<ColumnDef<Conversation>[]>(() => [
    {
      accessorKey: 'client',
      header: 'Cliente',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.original.client?.nombre}</span>
          <span className="text-xs text-muted-foreground">{row.original.client?.casinoUsername ?? '—'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const cfg = statusBadge[row.original.status] ?? { label: row.original.status, variant: 'secondary' as const };
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
      },
    },
    {
      accessorKey: 'lastMessagePreview',
      header: 'Último mensaje',
      cell: ({ row }) => (
        <span className="line-clamp-1 text-sm text-muted-foreground">{row.original.lastMessagePreview ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'unreadOperator',
      header: '',
      cell: ({ row }) =>
        row.original.unreadOperator > 0 ? (
          <Badge variant="destructive" className="rounded-full px-2 py-0.5 text-xs">
            {row.original.unreadOperator}
          </Badge>
        ) : null,
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setSelectedId(row.original.id)}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Abrir chat
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Archive className="mr-2 h-4 w-4" />
              Archivar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], []);

  const handleSend = async () => {
    if (!selectedId || !input.trim()) return;
    await sendMutation.mutateAsync({ conversationId: selectedId, content: input.trim(), type: 'text' });
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  return (
    <>
      <TopBanner
        title="Chats"
        subtitle="Conversaciones en curso"
        searchPlaceholder="Buscar cliente..."
        searchValue={search}
        onSearchChange={setSearch}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Left: conversation list */}
        <div className={`flex flex-col border-r border-border bg-background ${selectedId ? 'hidden w-0 lg:flex lg:w-80 xl:w-96' : 'w-full lg:w-80 xl:w-96'}`}>
          <div className="flex items-center gap-2 overflow-x-auto border-b border-border p-3">
            {(['all', 'open', 'closed', 'archived'] as StatusFilter[]).map((s) => (
              <Button
                key={s}
                variant={status === s ? 'default' : 'ghost'}
                size="sm"
                className="shrink-0 text-xs capitalize"
                onClick={() => setStatus(s)}
              >
                {s === 'all' ? 'Todas' : statusBadge[s]?.label ?? s}
              </Button>
            ))}
          </div>
          <div className="flex-1 overflow-auto p-3">
            <DataTable
              columns={columns}
              data={rows}
              isLoading={isLoading}
              pageSize={20}
              emptyMessage="No hay conversaciones"
            />
          </div>
        </div>

        {/* Center: chat messages */}
        <div className={`flex flex-1 flex-col ${selectedId ? 'flex' : 'hidden lg:flex'}`}>
          {selected ? (
            <>
              {/* Chat header */}
              <div className="flex h-14 items-center gap-2 border-b border-border px-2 lg:px-4">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 shrink-0 lg:hidden"
                  onClick={() => setSelectedId(null)}
                  aria-label="Volver a conversaciones"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {(selected.client?.nombre?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{selected.client?.nombre}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Circle className="h-2 w-2 shrink-0 fill-green-500 text-green-500" />
                      <span className="truncate">{selected.client?.casinoUsername ?? '—'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-auto space-y-3 p-3 lg:p-4">
                {messages?.length ? (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.senderType === 'operator' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm lg:max-w-[75%] ${
                          msg.senderType === 'operator'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        <div>{msg.body}</div>
                        <div className="mt-1 text-[10px] opacity-70">
                          {new Date(msg.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Sin mensajes todavía
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="flex items-center gap-2 border-t border-border p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
                <Input
                  className="h-11 flex-1 lg:h-10"
                  placeholder="Escribí un mensaje..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sendMutation.isPending}
                />
                <Button
                  size="icon"
                  className="h-11 w-11 shrink-0 lg:h-10 lg:w-10"
                  onClick={() => void handleSend()}
                  disabled={sendMutation.isPending || !input.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground">
              <MessageSquare className="mb-2 h-10 w-10 opacity-30" />
              <p className="text-sm">Seleccioná una conversación para ver los mensajes</p>
            </div>
          )}
        </div>

        {/* Right: client info sidebar */}
        {selected?.client && (
          <div className="hidden w-72 border-l border-border bg-background xl:flex xl:flex-col">
            <div className="border-b border-border p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                  {(selected.client.nombre?.[0] ?? '?').toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-medium">{selected.client.nombre}</div>
                  <div className="text-xs text-muted-foreground">{selected.client.casinoUsername}</div>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-auto space-y-3 p-4">
              <InfoRow label="Usuario casino" value={selected.client.casinoUsername} />
              <InfoRow label="VIP" value={selected.client.isVip ? 'Sí' : 'No'} />
              <InfoRow label="Estado conversación" value={statusBadge[selected.status]?.label ?? selected.status} />
              <InfoRow label="Operador asignado" value={selected.operator?.displayName ?? 'Sin asignar'} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
